import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Box, Sparkles, Hash } from "lucide-react";
import { SectionHeading } from "../common/SectionHeading";
import { Reveal } from "../common/Reveal";
import { ProductCard } from "../common/ProductCard";
import { ProductInterest } from "./ProductInterest";
import { api } from "../../lib/api";

const BOX_STEPS = [
    { icon: Package, label: "Pick Product" },
    { icon: Box, label: "Choose Box Size" },
    { icon: Sparkles, label: "Select Flavors" },
    { icon: Hash, label: "Set Quantity" },
];

export const ShopPreview = () => {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/products")
            .then((res) => setProducts(res.data || []))
            .catch(() => {});
    }, []);

    return (
        <section data-testid="shop-preview-section" className="bg-white py-24 md:py-32">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHeading
                        chapter="05"
                        kicker="The Goods"
                        title="Packaged Showtime Is Coming."
                        copy="The show doesn't stop when the event ends. Cotton candy. Popcorn. Mystery flavors. Party-ready packs. We're working on the first Showtime drops now."
                        testid="shop-preview-heading"
                    />
                    <Reveal delay={0.1} className="mb-14 md:mb-20">
                        <Link
                            to="/shop"
                            data-testid="shop-all-link"
                            className="inline-flex items-center gap-2 font-oswald uppercase tracking-widest text-sm text-ink border-b-2 border-pink pb-1 hover:text-pink-dark transition-colors duration-300"
                        >
                            Shop All <ArrowRight size={15} />
                        </Link>
                    </Reveal>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {products.slice(0, 4).map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                    <Reveal className="col-span-2 lg:col-span-4" delay={0.1}>
                        <div
                            data-testid="build-a-box-banner"
                            className="relative overflow-hidden rounded-3xl bg-ink tent-stripes text-white p-8 md:p-12 flex flex-col lg:flex-row lg:items-center gap-8"
                        >
                            <div className="flex-1">
                                <p className="font-manrope text-xs font-bold tracking-[0.35em] uppercase text-pink mb-3">Future Favorite</p>
                                <h3 className="font-oswald uppercase text-3xl md:text-5xl leading-none">Build a Box</h3>
                                <p className="mt-3 text-zinc-400 text-sm md:text-base max-w-md">
                                    Your box, your rules. Full Build-a-Box launches soon — here's how it'll play.
                                </p>
                            </div>
                            <ol className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                                {BOX_STEPS.map((step, i) => (
                                    <li key={step.label} className="flex flex-col items-start gap-2">
                                        <span className="h-10 w-10 rounded-full border border-pink text-pink flex items-center justify-center">
                                            <step.icon size={16} />
                                        </span>
                                        <span className="font-oswald uppercase text-xs tracking-widest text-zinc-300">
                                            {i + 1}. {step.label}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                            <Link
                                to="/shop"
                                data-testid="build-a-box-cta"
                                className="shrink-0 inline-flex items-center gap-2 rounded-full bg-pink px-7 py-3.5 font-oswald uppercase tracking-widest text-sm text-ink hover:bg-white transition-colors duration-300 active:scale-95"
                            >
                                Start Your Box <ArrowRight size={15} />
                            </Link>
                        </div>
                    </Reveal>
                    {products.slice(4).map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
                <ProductInterest />
            </div>
        </section>
    );
};
